// @flow
import * as React from 'react';

type EnterTransitions = 'enter' | 'entering' | 'entered';
type ExitTransitions = 'exit' | 'exiting' | 'exited';

type TimeoutMap = {|
  enter: number,
  exit: number,
|};

type UseTransitionArgs = {|
  /** The initial value. If true, then the state should transition from 'enter' ->  'entering' -> 'entered.
   *  If false, then it denotes the state should transition from 'exit' ->  'exiting' -> 'exited' */
  in: boolean,
  /** The timeout of each transition */
  timeout: number | TimeoutMap,
  /** If true, begin the transition if the in property is initialized to true. Defaults to true */
  appear?: boolean,
  /** If true, then transition changes forces a document reflow which are necessary to for css transitions to apply.
   *  This should be false and only be set to true if you notice css transitions aren't applying properly  */
  forceDocumentReflow?: boolean,
|};

/**
 * --------------------------------------------------------------------------------------
 * 'enter' - signifies the initial entering transition point
 * 'entering' -  signifies entering state while the transition is ongoing
 * 'entered - final state and signifies state after enter transitioning is finished
 * --------------------------------------------------------------------------------------
 * 'exit' - signifies the initial exiting transition point
 * 'exiting' -signifies the exiting state while the transition is ongoing
 * 'exited' - final state and signifies state after exit transitioning is finished
 * */
type UseTransitionState = EnterTransitions | ExitTransitions;

const computeInitialTransition = (inProp: boolean, appear: boolean) => {
  if (!inProp) {
    return 'exited';
  }
  if (appear) {
    return 'enter';
  }
  return 'entered';
};

/**
 * Inspired by react-transition-group's CSSTransition/Transition component.
 * A light-weight hook which exposes transition states that is also friendly to Lithium
 */
const useTransition = ({
  in: inProp,
  timeout,
  appear = true,
  forceDocumentReflow,
}: UseTransitionArgs): UseTransitionState => {
  const [transition, setTransition] = React.useState<UseTransitionState>(
    computeInitialTransition(inProp, appear),
  );
  const enterTimeout = typeof timeout === 'object' ? timeout.enter : timeout;
  const exitTimeout = typeof timeout === 'object' ? timeout.exit : timeout;

  const transitionTo = React.useCallback((to, time) => {
    if (time) {
      const timeoutId = setTimeout(() => {
        setTransition(to);
      }, time);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    setTransition(to);

    return () => {};
  }, []);

  const isEnter = transition === 'enter' || transition === 'entering';
  React.useEffect(() => {
    if (isEnter) {
      // Forces a document reflow
      if (forceDocumentReflow) {
        // eslint-disable-next-line no-restricted-globals,no-void
        void document?.documentElement?.offsetHeight;
      }
      const cleanup = [transitionTo('entering'), transitionTo('entered', enterTimeout)];
      return () => {
        cleanup.forEach(fn => fn());
      };
    }
    return () => {};
  }, [isEnter, transitionTo, enterTimeout, forceDocumentReflow]);

  const isExit = transition === 'exit' || transition === 'exiting';
  React.useEffect(() => {
    if (isExit) {
      // Forces a document reflow
      if (forceDocumentReflow) {
        // eslint-disable-next-line no-restricted-globals,no-void
        void document?.documentElement?.offsetHeight;
      }
      const cleanup = [transitionTo('exiting'), transitionTo('exited', exitTimeout)];
      return () => {
        cleanup.forEach(fn => fn());
      };
    }
    return () => {};
  }, [isExit, transitionTo, exitTimeout, forceDocumentReflow]);

  /* This is state derived from props: See https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops */
  /* The transition state should always follow the "in" property passed in */

  // A small optimization so the first rerender won't run; the correct value is computed in the initial state
  const [previousIn, setPreviousIn] = React.useState<boolean>(inProp);
  if (inProp !== previousIn) {
    // False -> True should derive to enter state
    if (!previousIn) {
      setTransition('enter');
    }
    // True -> False should derive to exit state
    else {
      setTransition('exit');
    }
    setPreviousIn(inProp);
  }
  return transition;
};

export default useTransition;
